import { useMemo, useState } from "react";
import { addDays, format, isAfter, isSameDay, subDays } from "date-fns";

import { env } from "~/env";
import { CalendarPopup } from "~/screens/Scheduling/InitialVisit/components/CalendarPopup";
import { calendarDayFromDate } from "~/screens/Scheduling/InitialVisit/utils/date-time-utils";
import type { CalendarDay } from "~/shared.types";
import { icons } from "~/ui/common/Icons";
import { generateCalendarDaysForMonth } from "~/utils";

const DATE_DISPLAY_FORMAT = "EEEE, MMMM do, yyyy";

type Props = {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  calendarMonth: { year: number; monthIndex: number };
  setCalendarMonth: (month: { year: number; monthIndex: number }) => void;
  isSelectableDay: (date: Date) => boolean;
  isPending: boolean;
};

export const DateNavigator = ({
  selectedDate,
  setSelectedDate,
  calendarMonth,
  setCalendarMonth,
  isSelectableDay,
  isPending,
}: Props) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const today = new Date();
  const maxAllowedDate = new Date(
    today.getFullYear(),
    today.getMonth() + Number(env.VITE_APPOINTMENT_SLOT_MAX_MONTHS) + 1,
    0,
  );

  const isPrevDisabled = isSameDay(selectedDate, today);
  const isNextDisabled =
    isSameDay(selectedDate, maxAllowedDate) ||
    isAfter(selectedDate, maxAllowedDate);

  const calendarDays = useMemo(
    () =>
      generateCalendarDaysForMonth(
        calendarMonth.year,
        calendarMonth.monthIndex,
      ),
    [calendarMonth.year, calendarMonth.monthIndex],
  );
  const selectedDay = useMemo(
    () =>
      calendarDays.find((d) => isSameDay(d.date, selectedDate)) ??
      calendarDayFromDate(selectedDate),
    [calendarDays, selectedDate],
  );

  const goToPreviousDay = () => setSelectedDate(subDays(selectedDate, 1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));

  const handleOpenCalendar = () => {
    setCalendarMonth({
      year: selectedDate.getFullYear(),
      monthIndex: selectedDate.getMonth(),
    });
    setIsCalendarOpen(true);
  };

  const handleCalendarDaySelect = (day: CalendarDay) => {
    setSelectedDate(day.date);
    setIsCalendarOpen(false);
  };

  const handleCalendarMonthChange = (monthYear: {
    month: string;
    year: number;
  }) => {
    const monthIndex = new Date(
      `${monthYear.month} 1, ${monthYear.year}`,
    ).getMonth();
    setCalendarMonth({ year: monthYear.year, monthIndex });
  };

  const handleCloseCalendar = () => {
    setIsCalendarOpen(false);
    setCalendarMonth({
      year: selectedDate.getFullYear(),
      monthIndex: selectedDate.getMonth(),
    });
  };

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <button
        type="button"
        onClick={handleOpenCalendar}
        className="text-indigo flex cursor-pointer disabled:pointer-events-none disabled:opacity-50"
        aria-label="Open calendar"
      >
        <icons.outline.CalendarIcon className="size-5" />
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={goToPreviousDay}
          disabled={isPrevDisabled}
          className="text-indigo flex cursor-pointer disabled:pointer-events-none disabled:text-neutral-300"
          aria-label="Previous day"
        >
          <icons.outline.ChevronLeftIcon className="size-5" strokeWidth={3} />
        </button>
        <span className="min-w-50 flex-1 text-center text-sm text-neutral-800">
          {format(selectedDate, DATE_DISPLAY_FORMAT)}
        </span>
        <button
          type="button"
          onClick={goToNextDay}
          disabled={isNextDisabled}
          className="text-indigo flex cursor-pointer disabled:pointer-events-none disabled:text-neutral-300"
          aria-label="Next day"
        >
          <icons.outline.ChevronRightIcon className="size-5" strokeWidth={3} />
        </button>
      </div>
      <div />
      {isCalendarOpen && (
        <CalendarPopup
          onClose={handleCloseCalendar}
          onDaySelect={handleCalendarDaySelect}
          calendarDays={calendarDays}
          isSelectable={isSelectableDay}
          initialDate={selectedDate}
          selectedDay={selectedDay}
          onMonthChange={handleCalendarMonthChange}
          className="absolute right-0 top-0 bg-transparent"
          isPending={isPending}
        />
      )}
    </div>
  );
};
